<template>
  <dashboard-layout title="Report Detail" back-to="/reports" back-label="Reports">
    <loading-state v-if="loading" label="Loading report details…" />

    <template v-else-if="report">
      <data-panel title="Report Type">
        <status-badge variant="danger" :label="report.report" />
      </data-panel>

      <data-panel title="Report Details">
        <div class="report-meta">
          <div class="meta-item">
            <span class="meta-label">Reported On</span>
            <span class="meta-value">{{ formatTimestamp(report.timestamp) }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">User ID</span>
            <router-link :to="`/user/${report.userId}`" class="user-id-link">
              {{ report.userId }}
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </router-link>
          </div>
        </div>
      </data-panel>

      <data-panel title="Reported Message">
        <div class="message-content">{{ report.message }}</div>
      </data-panel>

      <data-panel v-if="userData" title="User Information">
        <div class="user-info">
          <div class="user-avatar">{{ getUserInitials(userData.properties?.name) }}</div>
          <div class="user-details">
            <div class="user-name">{{ userData.properties?.name || 'Unknown User' }}</div>
            <router-link :to="`/user/${report.userId}`" class="view-profile-link">
              View Full Profile
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="10" r="3"></circle>
                <path d="M7 20.662V19c0-1 .9-2 2-2h6c1.1 0 2 1 2 2v1.662"></path>
              </svg>
            </router-link>
          </div>
        </div>
      </data-panel>
    </template>

    <div v-else class="not-found">
      <empty-state title="Report not found" hint="Report not found or an error occurred." icon="reports" />
      <router-link to="/reports" class="return-link">Return to Reports</router-link>
    </div>
  </dashboard-layout>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { FirebaseReportService } from '../services/firebaseReportService'
import { FirebaseUserService } from '../services/firebaseUserService'
import DashboardLayout from './ui/DashboardLayout.vue'
import DataPanel from './ui/DataPanel.vue'
import StatusBadge from './ui/StatusBadge.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'

export default {
  name: 'ReportDetailView',
  components: {
    DashboardLayout,
    DataPanel,
    StatusBadge,
    LoadingState,
    EmptyState
  },
  setup() {
    const route = useRoute()
    const reportService = new FirebaseReportService()
    const userService = new FirebaseUserService()

    const report = ref(null)
    const userData = ref(null)
    const loading = ref(true)

    const loadReport = async () => {
      try {
        const reportId = route.params.id
        const reportData = await reportService.getReportById(reportId)
        report.value = reportData

        if (reportData && reportData.userId) {
          const user = await userService.getUserById(reportData.userId)
          userData.value = user
        }
      } catch (error) {
        console.error('Error loading report:', error)
      } finally {
        loading.value = false
      }
    }

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return '-'

      // Handle Firestore timestamp objects
      if (timestamp.seconds) {
        const date = new Date(timestamp.seconds * 1000)
        return date.toLocaleString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      }

      // Handle regular Date objects or strings
      const date = new Date(timestamp)
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getUserInitials = (name) => {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    }

    onMounted(() => {
      loadReport()
    })

    return {
      report,
      userData,
      loading,
      formatTimestamp,
      getUserInitials
    }
  }
}
</script>

<style scoped>
.report-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: var(--field-bg);
  padding: 12px 16px;
  border-radius: 12px;
  min-width: 200px;
}

.meta-label {
  font-size: 12px;
  color: var(--text-secondary);
}

.meta-value {
  font-size: 14px;
  color: var(--text-primary);
}

.user-id-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--primary-color);
  text-decoration: none;
  transition: all 0.3s ease;
}

.user-id-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.user-id-link svg {
  width: 14px;
  height: 14px;
}

.message-content {
  background: var(--field-bg);
  padding: 16px;
  border-radius: 12px;
  line-height: 1.6;
  color: var(--text-primary);
  white-space: pre-wrap;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
  background: var(--field-bg);
  padding: 16px;
  border-radius: 12px;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.user-name {
  font-weight: 600;
  color: var(--text-primary);
}

.view-profile-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--primary-color);
  font-size: 14px;
  text-decoration: none;
  transition: all 0.3s ease;
  width: fit-content;
}

.view-profile-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.not-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.return-link {
  padding: 8px 16px;
  background: rgba(var(--primary-rgb), 0.2);
  border-radius: 8px;
  color: var(--primary-color);
  text-decoration: none;
  transition: all 0.3s ease;
}

.return-link:hover {
  background: rgba(var(--primary-rgb), 0.3);
}

@media (max-width: 1024px) {
  .meta-item {
    min-width: 100%;
  }
}
</style>
